import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AncoraDePendencias, CabecalhoDaFicha, EscolhaDeBalanca, EtapasDaFicha,
  FaixaDaFicha, LaudoDaFicha, RodapeDeEtapas, fichaCongelada, useEquipamentos,
  useEtapa, useFicha,
} from '../Ficha';
import type { EquipamentoDaLista, Etapa, Indicador } from '../Ficha';
import { Pendencias } from '../Pendencias';
import { ErroApi, mensagemDoErro } from '../api';
import {
  verMr, gravarMrCabecalho, gravarMrCorpos, descartarMrCiclo,
  importarBrutoMr, enviarFotosMr,
} from '../api/mr';
import { CapturaDeFoto } from '../CapturaDeFoto';
import { gabaritoDoMetodo } from '../gabaritos';
import type { EnsaioMr, ConferenciaMr } from '../api/mr';
import { numero, paraNumero, paraCampo, rotulo } from '../formato';
import { tecladoDaTabela } from '../tecladoDaTabela';
import {
  Aviso, Campo, Carregando, ErroDaApi, Escolha,
  EscolhaPorCodigo, Selo, ZonaDeArquivo, comoCodigo, RespostaDaAcao,
} from '../ui';
import type { Opcao } from '../ui';

/**
 * Lançar o módulo de resiliência — DNIT 181/2018-ME.
 *
 * ─── O caminho normal é o arquivo ────────────────────────────────────
 *
 * A prensa dinâmica grava uma planilha com um ciclo por linha, e um
 * arquivo do dia traz VÁRIOS corpos de prova — às vezes de obras
 * diferentes. Por isso a tela não importa direto: ela lê o arquivo,
 * mostra o que ele tem, e quem lança marca quais corpos de prova são
 * deste ensaio. Importar tudo seria misturar obras num laudo só.
 *
 * ─── O que a tela NÃO calcula ────────────────────────────────────────
 *
 * Nada. ε, MR, K₁, K₂, R², a faixa do item 8 e a triplicata do item 9
 * vêm prontos do banco (`D-29`), conferidos contra a planilha do
 * relatório 2607315 da Testesolo antes de existirem. Uma segunda conta
 * aqui seria uma segunda resposta para a mesma pergunta — e a da tela é
 * a que a pessoa vê.
 *
 * ─── A altura de referência do LVDT ──────────────────────────────────
 *
 * É o campo mais perigoso desta ficha, e por isso ele é o único que a
 * tela explica por extenso. Ela NÃO é a altura do corpo de prova: as
 * alças ficam a 3/4 médio, e o que se mede é o encurtamento do trecho
 * entre elas. Trocar uma pela outra muda o módulo em 25 % num CP de
 * 100 × 200 — e o resultado continua parecendo um resultado.
 */

const CAT_PRENSA = ['prensa_dinamica', 'prensa_triaxial', 'multiprensa'];

interface Equipamento {
  id: string;
  codigo_patrimonial: string;
  nome: string;
  categoria: string;
}

const num = (v: unknown, casas: number) => numero(v, casas);

export function Mr() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioMr | null>(null);
  const equipamentos = useEquipamentos();
  const equip: Equipamento[] = equipamentos;
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try { setD(await verMr(id)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<EnsaioMr>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      setD(await chamada());
      setOk('Gravado.');
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, []);

  const campoErro = (c: string) =>
    (erro instanceof ErroApi ? erro.campo(c) : undefined);

  const ficha = useFicha(id, undefined, ok);
  const cps = d?.corpos?.length ?? 0;
  const temCabecalho = !!d?.cabecalho;
  const temResultado = d?.cabecalho?.mr_triplicata_mpa != null;
  const [etapa, irPara] = useEtapa(4,
    !temCabecalho ? 1 : cps === 0 || !d?.balanca ? 2 : !temResultado ? 3 : 4);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o ensaio" />;

  const c = d.cabecalho ?? {};
  const congelado = fichaCongelada(ficha?.status);

  /*
   * No Carbon (onda 238): quatro etapas — a mistura e as condições, os
   * corpos de prova (o arquivo da prensa e a tabela, com a balança das
   * massas — RN-215), o resultado, e as fotos e o laudo. As casas são as
   * que o laudo do MR já imprime (MR com uma casa, em MPa).
   */
  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCabecalho,
      resumo: temCabecalho ? (c.material ?? c.norma_referencia ?? 'gravadas') : 'a gravar' },
    { nome: 'Corpos de prova', feita: cps > 0 && !!d.balanca,
      falta: cps > 0 && !d.balanca,
      resumo: cps ? `${cps} corpo(s) de prova` : 'importe o arquivo' },
    { nome: 'Resultado', feita: temResultado,
      resumo: c.triplicata_aceita === true ? 'triplicata aceita'
        : c.triplicata_aceita === false ? 'fora de ±10 %' : undefined },
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];
  const indicadores: Indicador[] = [
    { rotulo: 'MR · média da triplicata', chave: true, unidade: 'MPa',
      valor: c.mr_triplicata_mpa != null ? num(c.mr_triplicata_mpa, 1) : null,
      porque: 'Sai com a triplicata de referência completa.',
      como: 'A média dos corpos de prova de referência (item 9) — calculada pelo banco.' },
    { rotulo: 'CPs de referência',
      valor: c.cps_total != null ? `${c.cps_referencia ?? 0} de ${c.cps_total}` : null },
    { rotulo: 'Desvio máximo', unidade: '%',
      valor: c.desvio_maximo != null ? num(Number(c.desvio_maximo) * 100, 2) : null,
      nota: c.triplicata_aceita === true ? '±10 % atendido'
        : c.triplicata_aceita === false ? 'fora de ±10 %' : undefined },
    { rotulo: 'Arquivos importados', valor: String(d.arquivos?.length ?? 0) },
  ];

  return (
    <div className="tela mr ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'MR'}
        titulo="Módulo de resiliência" ficha={ficha}
        normaDaTela={c.norma_referencia ?? 'DNIT 181/2018-ME'} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />

      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>

      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <Cabecalho d={d} id={id} gravar={gravar} salvando={salvando}
          equip={equip} campoErro={campoErro} />
      )}
      {etapa === 2 && (
        <>
          <Importar id={id} recarregar={carregar} temCabecalho={!!d.cabecalho} />
          <CorposDeProva d={d} id={id} gravar={gravar} salvando={salvando}
            equipamentos={equipamentos} campoErro={campoErro} />
        </>
      )}
      {etapa === 3 && (
        d.corpos.length
          ? <Resultado d={d} />
          : <Aviso tipo="info">Sem corpo de prova ainda — importe o arquivo na etapa 2.</Aviso>
      )}
      {etapa === 4 && (
        <>
          <FotosDosCps d={d} id={id} recarregar={carregar} />
          <LaudoDaFicha id={id} />
        </>
      )}
      </fieldset>

      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

// ── 1 · cabeçalho ────────────────────────────────────────────────────

function Cabecalho({ d, id, gravar, salvando, equip, campoErro }: {
  d: EnsaioMr; id: string;
  gravar(m: string, ch: () => Promise<EnsaioMr>): Promise<boolean>;
  salvando: string | null;
  equip: Equipamento[];
  campoErro(c: string): string | undefined;
}) {
  const c = d.cabecalho ?? {};
  const [f, setF] = useState({
    material: c.material ?? '',
    traco: c.traco ?? '',
    estabilizante: c.estabilizante ?? '',
    jazida: c.jazida ?? '',
    teor: paraCampo(c.teor_estabilizante_pct),
    origemCp: c.origem_cp ?? 'fornecido',
    energia: c.energia_compactacao ?? '',
    deltaMin: paraCampo(c.delta_min_mm ?? 0.025),
    deltaMax: paraCampo(c.delta_max_mm ?? 0.05),
    prensa: c.prensa_equipamento_id ?? '',
    observacoes: c.observacoes ?? '',
  });
  const p = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  const prensas: Opcao[] = equip
    .filter((e) => CAT_PRENSA.includes(e.categoria))
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial, detalhe: e.nome }));

  return (
    <section className="bloco">
      <h2>1 · A mistura e as condições</h2>
      <p className="sub">
        O cabeçalho vale para TODOS os corpos de prova deste ensaio: a
        mistura é uma só, e o item 9 compara corpos de prova da mesma
        mistura.
      </p>
      <div className="grade">
        <Campo rotulo="Material" value={f.material}
          erro={campoErro('material')}
          onChange={(e) => p('material')(e.target.value)} />
        <Campo rotulo="Traço" value={f.traco}
          onChange={(e) => p('traco')(e.target.value)} />
        <Campo rotulo="Estabilizante" value={f.estabilizante}
          onChange={(e) => p('estabilizante')(e.target.value)} />
        <Campo rotulo="Teor do estabilizante (%)" inputMode="decimal"
          value={f.teor} onChange={(e) => p('teor')(e.target.value)} />
        <Campo rotulo="Jazida" value={f.jazida}
          onChange={(e) => p('jazida')(e.target.value)} />
        <Escolha
          rotulo="Origem do corpo de prova"
          valor={f.origemCp}
          aoEscolher={p('origemCp')}
          opcoes={[
            { valor: 'fornecido', texto: 'Fornecido pelo contratante',
              detalhe: 'sem energia de compactação nossa' },
            { valor: 'moldado', texto: 'Moldado no laboratório' },
          ]}
        />
        <Campo rotulo="Energia de compactação" value={f.energia}
          dica="Como o cliente a descreve — «100% Proctor Intermediária»."
          onChange={(e) => p('energia')(e.target.value)} />
        <EscolhaPorCodigo
          rotulo="Prensa dinâmica"
          valor={f.prensa}
          aoEscolher={p('prensa')}
          opcoes={prensas.map(comoCodigo)}
          dica={prensas.length === 0
            ? 'Nenhuma prensa dinâmica cadastrada no parque.'
            : 'O laudo diz em que equipamento o ensaio foi feito.'}
        />
        <Campo rotulo="Item 8 — mínimo (mm)" inputMode="decimal"
          value={f.deltaMin} dica="A DNIT 181 pede 0,025 mm (25 µm)."
          onChange={(e) => p('deltaMin')(e.target.value)} />
        <Campo rotulo="Item 8 — máximo (mm)" inputMode="decimal"
          value={f.deltaMax} dica="A DNIT 181 pede 0,050 mm (50 µm)."
          onChange={(e) => p('deltaMax')(e.target.value)} />
      </div>
      <label className="campo">
        <span className="rotulo">Observações do ensaio</span>
        <textarea rows={3} value={f.observacoes}
          onChange={(e) => p('observacoes')(e.target.value)} />
        <span className="dica">
          Vai para TODAS as folhas do laudo. É onde entra, por exemplo, a
          posição das alças do LVDT quando ela difere do item 7b da norma —
          o sistema não escreve conclusão técnica sozinho.
        </span>
      </label>
      <div className="acoes">
        <button
          type="button" className="botao"
          disabled={salvando === 'cab'}
          onClick={() => void gravar('cab', () => gravarMrCabecalho(id, {
            material: f.material || null,
            traco: f.traco || null,
            estabilizante: f.estabilizante || null,
            jazida: f.jazida || null,
            teorEstabilizantePct: paraNumero(f.teor),
            origemCp: f.origemCp,
            energiaCompactacao: f.energia || null,
            deltaMinMm: paraNumero(f.deltaMin) ?? undefined,
            deltaMaxMm: paraNumero(f.deltaMax) ?? undefined,
            prensaEquipamentoId: f.prensa || null,
            observacoes: f.observacoes || null,
          }))}
        >
          {salvando === 'cab' ? 'Gravando…' : 'Gravar cabeçalho'}
        </button>
      </div>
    </section>
  );
}

// ── 2 · o arquivo da prensa ──────────────────────────────────────────

function Importar({ id, recarregar, temCabecalho }: {
  id: string; recarregar(): void; temCabecalho: boolean;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [conf, setConf] = useState<ConferenciaMr | null>(null);
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<'ler' | 'gravar' | null>(null);

  const ler = async () => {
    if (!arquivo) return;
    setOcupado('ler'); setErro(null); setFeito(null);
    try {
      const r = await importarBrutoMr(id, arquivo, { simular: true });
      setConf(r);
      setEscolhidos(r.corpos.map((x) => x.chave));
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(null); }
  };

  const importar = async () => {
    if (!arquivo || !escolhidos.length) return;
    setOcupado('gravar'); setErro(null);
    try {
      const r = await importarBrutoMr(id, arquivo, { testes: escolhidos });
      setFeito(`${r.cpsImportados} corpo(s) de prova e `
        + `${r.ciclosImportados} ciclo(s) importados.`);
      setConf(null); setArquivo(null);
      recarregar();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(null); }
  };

  return (
    <section className="bloco">
      <h2>2 · O arquivo da prensa</h2>
      <p className="sub">
        O arquivo entra imutável e fica. A leitura mostra o que ele tem
        ANTES de gravar: um arquivo do dia costuma trazer o ensaio de
        outra obra junto.
      </p>

      {!temCabecalho && (
        <Aviso tipo="atencao">
          Grave o cabeçalho antes de importar — os corpos de prova precisam
          de onde pendurar.
        </Aviso>
      )}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {feito && <Aviso tipo="ok">{feito}</Aviso>}

      <ZonaDeArquivo
        rotulo="Arquivo da prensa dinâmica"
        aceita=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream"
        tipos="XLSX"
        arquivos={arquivo}
        desabilitado={!temCabecalho || ocupado !== null}
        textoVazio="Arraste aqui o arquivo que saiu da prensa"
        dica="A planilha da MS 151, como o equipamento gravou. Ela fica
          guardada sem alteração."
        aoEscolher={(fs) => {
          setArquivo(fs[0] ?? null); setConf(null); setErro(null);
          setFeito(null);
        }}
      />

      <div className="acoes">
        <button type="button" className="botao secundario"
          disabled={!arquivo || ocupado !== null} onClick={() => void ler()}>
          {ocupado === 'ler' ? 'Lendo…' : 'Ler o arquivo'}
        </button>
      </div>

      {conf && (
        <div className="conferencia-mr">
          <p className="sub">
            {conf.arquivo} · aba {conf.aba} · {conf.linhasLidas} ciclo(s) em{' '}
            {conf.corpos.length} corpo(s) de prova. Marque os deste ensaio.
          </p>
          <table className="dados">
            <thead>
              <tr>
                <th />
                <th>Nº teste</th>
                <th>Identificação</th>
                <th className="n">Ciclos</th>
                <th className="n">σd (MPa)</th>
                <th className="n">Ø × h (mm)</th>
              </tr>
            </thead>
            <tbody>
              {conf.corpos.map((x) => (
                <tr key={x.chave}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Importar ${x.identificacao}`}
                      checked={escolhidos.includes(x.chave)}
                      onChange={(e) => setEscolhidos((l) => (e.target.checked
                        ? [...l, x.chave] : l.filter((k) => k !== x.chave)))}
                    />
                  </td>
                  <td className="mono">{x.numeroTeste ?? '—'}</td>
                  <td>{x.identificacao}</td>
                  <td className="n">{x.ciclos}</td>
                  <td className="n">
                    {num(x.tensaoDesvioMinMpa, 2)} – {num(x.tensaoDesvioMaxMpa, 2)}
                  </td>
                  <td className="n">
                    {num(x.diametroMm, 0)} × {num(x.alturaMm, 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="acoes">
            <button type="button" className="botao"
              disabled={!escolhidos.length || ocupado !== null}
              onClick={() => void importar()}>
              {ocupado === 'gravar' ? 'Importando…'
                : `Importar ${escolhidos.length} corpo(s) de prova`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── 3 · os corpos de prova ───────────────────────────────────────────

function CorposDeProva({ d, id, gravar, salvando, equipamentos, campoErro }: {
  d: EnsaioMr; id: string;
  gravar(m: string, ch: () => Promise<EnsaioMr>): Promise<boolean>;
  salvando: string | null;
  equipamentos: EquipamentoDaLista[];
  campoErro(c: string): string | undefined;
}) {
  /* A balança das massas dos CPs (RN-215, onda 238) — uma para todos. */
  const [balanca, setBalanca] = useState(d.balanca?.id ?? '');
  useEffect(() => { setBalanca(d.balanca?.id ?? ''); }, [d.balanca?.id]);
  const [linhas, setLinhas] = useState(() => d.corpos.map((cp) => ({
    ordem: Number(cp.ordem),
    identificacao: cp.identificacao ?? '',
    numeroTeste: paraCampo(cp.numero_teste),
    dataMoldagem: cp.data_moldagem ? String(cp.data_moldagem).slice(0, 10) : '',
    tempoCuraDias: paraCampo(cp.tempo_cura_dias),
    dataEnsaio: cp.data_ensaio ? String(cp.data_ensaio).slice(0, 10) : '',
    d1: paraCampo(cp.diametro_d1_mm),
    d2: paraCampo(cp.diametro_d2_mm),
    h1: paraCampo(cp.altura_h1_mm),
    h2: paraCampo(cp.altura_h2_mm),
    massa: paraCampo(cp.massa_cp_g ?? cp.massa_g),
    umidade: paraCampo(cp.teor_umidade_pct),
    curso: paraCampo(cp.curso_lvdt_mm),
    posicao: cp.posicao_lvdt ?? '',
    href: paraCampo(cp.altura_referencia_lvdt_mm),
    observacoes: cp.observacoes ?? '',
  })));

  useEffect(() => {
    setLinhas(d.corpos.map((cp) => ({
      ordem: Number(cp.ordem),
      identificacao: cp.identificacao ?? '',
      numeroTeste: paraCampo(cp.numero_teste),
      dataMoldagem: cp.data_moldagem ? String(cp.data_moldagem).slice(0, 10) : '',
      tempoCuraDias: paraCampo(cp.tempo_cura_dias),
      dataEnsaio: cp.data_ensaio ? String(cp.data_ensaio).slice(0, 10) : '',
      d1: paraCampo(cp.diametro_d1_mm),
      d2: paraCampo(cp.diametro_d2_mm),
      h1: paraCampo(cp.altura_h1_mm),
      h2: paraCampo(cp.altura_h2_mm),
      massa: paraCampo(cp.massa_cp_g ?? cp.massa_g),
      umidade: paraCampo(cp.teor_umidade_pct),
      curso: paraCampo(cp.curso_lvdt_mm),
      posicao: cp.posicao_lvdt ?? '',
      href: paraCampo(cp.altura_referencia_lvdt_mm),
      observacoes: cp.observacoes ?? '',
    })));
  }, [d.corpos]);

  const mudar = (i: number, k: string, v: string) =>
    setLinhas((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const novo = () => setLinhas((l) => [...l, {
    ordem: l.reduce((m, x) => Math.max(m, x.ordem), 0) + 1,
    identificacao: '', numeroTeste: '', dataMoldagem: '', tempoCuraDias: '',
    dataEnsaio: '', d1: '', d2: '', h1: '', h2: '', massa: '', umidade: '',
    curso: '', posicao: '', href: '', observacoes: '',
  }]);

  if (!linhas.length) {
    return (
      <section className="bloco">
        <h2>3 · Corpos de prova</h2>
        <p className="sub">
          Nenhum corpo de prova ainda. Importe o arquivo da prensa ou
          acrescente um à mão.
        </p>
        <div className="acoes">
          <button type="button" className="botao secundario" onClick={novo}>
            Acrescentar corpo de prova
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bloco">
      <h2>3 · Corpos de prova</h2>
      <div className="grade-campos fe-campos">
        <EscolhaDeBalanca rotulo="Balança das massas dos corpos de prova"
          equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
          erro={campoErro('balancaEquipamentoId')} />
      </div>
      <p className="sub">
        A geometria e a massa saem daqui — delas vem o peso específico que
        o laudo imprime. A altura de referência do LVDT é a que divide o
        deslocamento, e <b>não é a altura do corpo de prova</b>.
      </p>

      <div className="rolar-lado">
        <table className="dados mr-cps" {...tecladoDaTabela('Corpos de prova do MR')}>
          <thead>
            <tr>
              <th>CP</th><th>Identificação</th><th>Nº teste</th>
              <th>Moldagem</th><th>Cura (d)</th><th>Ensaio</th>
              <th className="n">d1</th><th className="n">d2</th>
              <th className="n">h1</th><th className="n">h2</th>
              <th className="n">Massa (g)</th><th className="n">w (%)</th>
              <th className="n">Curso LVDT</th><th>Posição</th>
              <th className="n">h ref. (mm)</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={l.ordem}>
                <td className="mono">{l.ordem}</td>
                <td>
                  <input value={l.identificacao} aria-label={`Identificação do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'identificacao', e.target.value)} />
                </td>
                <td>
                  <input value={l.numeroTeste} inputMode="numeric"
                    aria-label={`Número do teste do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'numeroTeste', e.target.value)} />
                </td>
                <td>
                  <input type="date" value={l.dataMoldagem}
                    aria-label={`Data de moldagem do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'dataMoldagem', e.target.value)} />
                </td>
                <td>
                  <input value={l.tempoCuraDias} inputMode="numeric"
                    aria-label={`Tempo de cura do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'tempoCuraDias', e.target.value)} />
                </td>
                <td>
                  <input type="date" value={l.dataEnsaio}
                    aria-label={`Data do ensaio do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'dataEnsaio', e.target.value)} />
                </td>
                {(['d1', 'd2', 'h1', 'h2', 'massa', 'umidade', 'curso'] as const)
                  .map((k) => (
                    <td key={k} className="n">
                      <input value={(l as any)[k]} inputMode="decimal"
                        aria-label={`${k} do CP ${l.ordem}`}
                        onChange={(e) => mudar(i, k, e.target.value)} />
                    </td>
                  ))}
                <td>
                  <input value={l.posicao} aria-label={`Posição do LVDT do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'posicao', e.target.value)} />
                </td>
                <td className="n">
                  <input value={l.href} inputMode="decimal"
                    aria-label={`Altura de referência do LVDT do CP ${l.ordem}`}
                    onChange={(e) => mudar(i, 'href', e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="acoes">
        <button type="button" className="botao secundario" onClick={novo}>
          Acrescentar corpo de prova
        </button>
        <button
          type="button" className="botao" data-gravar-tabela="" disabled={salvando === 'cps'}
          onClick={() => void gravar('cps', () => gravarMrCorpos(id, {
            ...(balanca ? { balancaEquipamentoId: balanca } : {}),
            corpos: linhas.map((l) => ({
              ordem: l.ordem,
              identificacao: l.identificacao || `CP ${l.ordem}`,
              numeroTeste: paraNumero(l.numeroTeste),
              dataMoldagem: l.dataMoldagem || null,
              tempoCuraDias: paraNumero(l.tempoCuraDias),
              dataEnsaio: l.dataEnsaio || null,
              diametroD1Mm: paraNumero(l.d1),
              diametroD2Mm: paraNumero(l.d2),
              alturaH1Mm: paraNumero(l.h1),
              alturaH2Mm: paraNumero(l.h2),
              massaCpG: paraNumero(l.massa),
              teorUmidadePct: paraNumero(l.umidade),
              cursoLvdtMm: paraNumero(l.curso),
              posicaoLvdt: l.posicao || null,
              alturaReferenciaLvdtMm: paraNumero(l.href),
              observacoes: l.observacoes || null,
            })),
          }))}
        >
          {salvando === 'cps' ? 'Gravando…' : 'Gravar corpos de prova'}
        </button>
      </div>
    </section>
  );
}

// ── 4 · o resultado ──────────────────────────────────────────────────

function Resultado({ d }: { d: EnsaioMr }) {
  const c = d.cabecalho ?? {};
  const [descartando, setDescartando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const { id = '' } = useParams();

  if (!d.corpos.length) return null;

  return (
    <section className="bloco">
      <h2>4 · Resultado</h2>
      <p className="sub">
        Tudo aqui vem do banco: ε, MR, o item 8 e a triplicata do item 9.
        A tela não recalcula nada — o que ela mostra é o que o laudo
        imprime.
      </p>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      <div className="resumo-mr">
        <div><b>Corpos de prova de referência</b>{' '}
          {c.cps_referencia ?? 0} de {c.cps_total ?? 0}</div>
        <div><b>Média da triplicata</b>{' '}
          {num(c.mr_triplicata_mpa, 1)} MPa</div>
        <div><b>Desvio máximo</b>{' '}
          {c.desvio_maximo == null ? '—'
            : `${num(Number(c.desvio_maximo) * 100, 2)} %`}{' '}
          {c.triplicata_aceita === true ? <Selo tom="ok">±10 % atendido</Selo>
            : c.triplicata_aceita === false
              ? <Selo tom="critico">Fora de ±10 %</Selo>
              : <Selo tom="atencao">Triplicata incompleta</Selo>}
        </div>
      </div>

      {d.corpos.map((cp) => (
        <div className="cp-bloco" key={cp.id}>
          <h3>
            CP {cp.ordem} · {cp.identificacao}{' '}
            {cp.de_referencia ? <Selo tom="ok">Referência</Selo>
              : <Selo tom="atencao">Preliminar</Selo>}
          </h3>
          <p className="sub">
            MR médio <b>{num(cp.mr_medio_mpa, 1)} MPa</b> ·{' '}
            K₁ {num(cp.k1, 1)} · K₂ {num(cp.k2, 4)} · R² {num(cp.r2, 4)} ·{' '}
            item 8: {cp.ciclos_item8} de {cp.ciclos_total}
            {cp.desvio_vs_triplicata != null
              && ` · desvio ${num(Number(cp.desvio_vs_triplicata) * 100, 2)} %`}
          </p>
          <div className="rolar-lado">
            <table className="dados">
              <thead>
                <tr>
                  <th className="n">Ciclo</th>
                  <th className="n">σ3 (MPa)</th>
                  <th className="n">σd (MPa)</th>
                  <th className="n">δ (mm)</th>
                  <th>Item 8</th>
                  <th className="n">ε (%)</th>
                  <th className="n">MR (MPa)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.ciclos.filter((x) => x.corpo_prova_id === cp.id).map((x) => (
                  <tr key={x.id} className={x.descartado ? 'descartada' : ''}>
                    <td className="n">{x.ciclo}</td>
                    <td className="n">{num(x.tensao_confinante_mpa, 3)}</td>
                    <td className="n">{num(x.tensao_desvio_mpa, 4)}</td>
                    <td className="n">{num(x.deslocamento_recuperavel_mm, 5)}</td>
                    <td>
                      {x.atende_item8 ? <Selo tom="ok">Dentro</Selo>
                        : <Selo tom="atencao">Fora</Selo>}
                    </td>
                    <td className="n">{num(x.deformacao_resiliente_pct, 5)}</td>
                    <td className="n"><b>{num(x.mr_mpa, 1)}</b></td>
                    <td>
                      {x.descartado
                        ? <span className="sub">{x.motivo_descarte}</span>
                        : (
                          <button type="button" className="botao discreto"
                            onClick={() => { setDescartando(x.id); setMotivo(''); }}>
                            Descartar
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {descartando && (
        <div className="cartao confirmacao">
          <p>
            O ciclo continua no laudo, riscado e com o motivo. Descartar não
            é apagar — o que sai da conta fica à vista dizendo que saiu.
          </p>
          <Campo rotulo="Motivo do descarte" value={motivo}
            onChange={(e) => setMotivo(e.target.value)} />
          <div className="acoes">
            <button type="button" className="botao discreto"
              onClick={() => setDescartando(null)}>Cancelar</button>
            <button type="button" className="botao"
              disabled={motivo.trim().length < 5}
              onClick={() => {
                const alvo = descartando;
                setDescartando(null);
                descartarMrCiclo(id, alvo, motivo)
                  .then(() => window.location.reload())
                  .catch((e) => setErro(mensagemDoErro(e)));
              }}>
              Descartar o ciclo
            </button>
          </div>
        </div>
      )}
    </section>
  );
}


// ── 5 · as fotos, uma folha por corpo de prova ───────────────────────

/**
 * As fotos do corpo de prova, e de QUAL corpo de prova.
 *
 * «para as fotos deverá ter um botão para tirar a foto na hora ou
 *  anexar. Mas a ideia é que a foto seja tirada na hora (…) Para outros
 *  ensaios, como triaxial, cisalhamento direto, terão outros gabaritos
 *  com as formas correspondentes de cada CP.» — Humberto, 02/09/2026.
 *
 * O gabarito aqui é o CILINDRO em pé — é assim que se fotografa um corpo
 * de prova de 100 × 200 —, e não o círculo do adensamento.
 *
 * Cada CP tem o par dele: antes e depois. O laudo tem uma folha por
 * corpo de prova, e a foto que não diz de qual CP é vai para a folha
 * errada — o que é pior do que foto nenhuma, porque parece evidência.
 */
function FotosDosCps({ d, id, recarregar }: {
  d: EnsaioMr; id: string; recarregar(): void;
}) {
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (!d.corpos.length) return null;

  const gabarito = gabaritoDoMetodo('MR');
  const vivas = (d.fotos ?? []).filter((f: any) => !f.desconsiderada);
  const dasDoCp = (ordem: number) => vivas.filter(
    (f: any) => Number(f.corpo_prova_ordem) === Number(ordem));

  const enviar = async (
    cpOrdem: number, tipo: 'cp_antes' | 'cp_depois', arquivo: File,
  ) => {
    const marca = `${cpOrdem}-${tipo}`;
    setOcupado(marca); setErro(null);
    try {
      await enviarFotosMr(id, [{ arquivo, tipo, corpoProvaOrdem: cpOrdem }]);
      recarregar();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(null); }
  };

  return (
    <section className="bloco">
      <h2>5 · Fotos dos corpos de prova</h2>
      <p className="sub">
        Cada corpo de prova tem a folha dele no laudo, e as fotos vão
        para a folha do CP que você indicar aqui. Fotografe na moldagem e
        no desmonte — depois do descarte não há segunda chance.
      </p>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {d.corpos.map((cp: any) => {
        const minhas = dasDoCp(cp.ordem);
        const tem = (t: string) => minhas.some((f: any) => f.tipo === t);
        return (
          <div className="cp-bloco" key={cp.id}>
            <h3>
              CP {cp.ordem} · {cp.identificacao}{' '}
              {minhas.length === 0
                ? <Selo tom="atencao">Sem foto</Selo>
                : <Selo tom="ok">{minhas.length}</Selo>}
            </h3>
            <div className="galeria-envio">
              {([
                { tipo: 'cp_antes' as const, nome: 'Antes do ensaio' },
                { tipo: 'cp_depois' as const, nome: 'Depois do ensaio' },
              ]).map((p) => (
                <div className="campo" key={p.tipo}>
                  <span className="rotulo">
                    {p.nome}{' '}
                    {tem(p.tipo)
                      ? <Selo tom="ok">Anexada</Selo>
                      : <Selo tom="atencao">Falta</Selo>}
                  </span>
                  <CapturaDeFoto
                    tipo={p.tipo}
                    titulo={`CP ${cp.ordem} — ${p.nome}`}
                    gabarito={gabarito}
                    desabilitado={ocupado !== null}
                    aoReceber={(arquivo) => enviar(cp.ordem, p.tipo, arquivo)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}
